import { z } from 'zod'

/**
 * Todo lo que se puede registrar. La lista es cerrada a propósito: cada tipo
 * nuevo obliga a decidir qué significa al leerlo.
 *
 * Ninguno dura: todos son instantáneos. Por eso el evento no lleva `endedAt`
 * ni `running`; sin tipos duraderos, esas dos columnas sólo servirían para
 * crear estados imposibles (en curso y terminado a la vez).
 */
export const EVENT_TYPES = [
  'recipe',
  'meal_plan',
  'meal_eaten',
  'shopping_extra',
  'shopping_check',
] as const

export type EventType = (typeof EVENT_TYPES)[number]

/** Las franjas del día que se planifican, en orden. */
export const MEAL_SLOTS = ['desayuno', 'comida', 'merienda', 'cena'] as const
export type MealSlot = (typeof MEAL_SLOTS)[number]

/**
 * Los grupos de alimentos. Tres de ellos forman la proporción del plato; el
 * lácteo y la grasa se enseñan pero no puntúan (nadie sirve "una porción de
 * aceite": es técnica de cocina, no una ración).
 */
export const FOOD_GROUPS = [
  'verdura',
  'fruta',
  'carne',
  'pescado',
  'huevo',
  'legumbre',
  'cereal',
  'lacteo',
  'grasa',
] as const
export type FoodGroup = (typeof FOOD_GROUPS)[number]

/**
 * Los cuatro tipos de proteína. Se miden por **variedad a lo largo de la
 * semana**, no por proporción dentro de un día: son alternativas entre sí, no
 * porciones que convivan en el mismo plato.
 */
export const PROTEIN_GROUPS = ['carne', 'pescado', 'huevo', 'legumbre'] as const
export type ProteinGroup = (typeof PROTEIN_GROUPS)[number]

/** Grupos que se registran pero quedan fuera del cálculo del equilibrio. */
export const INFORMATIVE_GROUPS = ['lacteo', 'grasa'] as const

export const FOOD_GROUP_LABELS: Record<FoodGroup, string> = {
  verdura: 'Verdura',
  fruta: 'Fruta',
  carne: 'Carne',
  pescado: 'Pescado',
  huevo: 'Huevo',
  legumbre: 'Legumbre',
  cereal: 'Cereal',
  lacteo: 'Lácteo',
  grasa: 'Grasa',
}

/**
 * Los platos de una franja, en el orden en que se sirven.
 *
 * Una comida puede llevar primero y segundo, la misma carne puede ir con una
 * guarnición distinta cada vez y el postre es un plato más. Lo que se escribió
 * antes de que existieran se lee como `segundo`: era el plato de la franja.
 */
export const COURSES = ['primero', 'segundo', 'guarnicion', 'postre'] as const
export type Course = (typeof COURSES)[number]
export const DEFAULT_COURSE: Course = 'segundo'

export const COURSE_LABELS: Record<Course, string> = {
  primero: 'Primero',
  segundo: 'Segundo',
  guarnicion: 'Guarnición',
  postre: 'Postre',
}

export const MEAL_SLOT_LABELS: Record<MealSlot, string> = {
  desayuno: 'Desayuno',
  comida: 'Comida',
  merienda: 'Merienda',
  cena: 'Cena',
}

/** Secciones del supermercado, en el orden en que se recorre la tienda. */
export const SHOPPING_SECTIONS = [
  'fresco',
  'carniceria',
  'pescaderia',
  'lacteos',
  'panaderia',
  'despensa',
  'congelados',
  'bebidas',
  'hogar',
  'otros',
] as const
export type ShoppingSection = (typeof SHOPPING_SECTIONS)[number]

export const SHOPPING_SECTION_LABELS: Record<ShoppingSection, string> = {
  fresco: 'Fruta y verdura',
  carniceria: 'Carnicería',
  pescaderia: 'Pescadería',
  lacteos: 'Lácteos y huevos',
  panaderia: 'Panadería',
  despensa: 'Despensa',
  congelados: 'Congelados',
  bebidas: 'Bebidas',
  hogar: 'Hogar',
  otros: 'Otros',
}

/** La sección por defecto de cada grupo; el usuario puede sobreescribirla. */
export const SECTION_BY_GROUP: Record<FoodGroup, ShoppingSection> = {
  verdura: 'fresco',
  fruta: 'fresco',
  carne: 'carniceria',
  pescado: 'pescaderia',
  huevo: 'lacteos',
  legumbre: 'despensa',
  cereal: 'despensa',
  lacteo: 'lacteos',
  grasa: 'despensa',
}

export const UNITS = ['g', 'ml', 'ud', 'cda', 'cdta', 'pizca'] as const
export type Unit = (typeof UNITS)[number]

export const UNIT_LABELS: Record<Unit, string> = {
  g: 'g',
  ml: 'ml',
  ud: 'ud',
  cda: 'cucharada',
  cdta: 'cucharadita',
  pizca: 'pizca',
}

export const ingredientSchema = z.object({
  name: z.string().min(1).max(120),
  quantity: z.number().min(0).max(100_000).nullable().default(null),
  unit: z.enum(UNITS).default('g'),
  /**
   * El peso que cuenta para la proporción del plato. Es nulo cuando no se
   * puede saber (una pizca, o unidades de un alimento sin peso conocido); el
   * cálculo lo dice en vez de suponer un número, que es como se inventan datos.
   */
  grams: z.number().min(0).max(100_000).nullable().default(null),
  group: z.enum(FOOD_GROUPS).nullable().default(null),
  /** Identificador en USDA FoodData Central, cuando se eligió de la base. */
  fdcId: z.number().int().positive().nullable().default(null),
  /** Sólo si se corrige a mano la sección que toca por grupo. */
  section: z.enum(SHOPPING_SECTIONS).nullable().default(null),
})

export type Ingredient = z.infer<typeof ingredientSchema>

const dayKeySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'día con formato AAAA-MM-DD')

/** Un plato servido: una receta o un texto libre, en su lugar de la comida. */
export const dishSchema = z.object({
  course: z.enum(COURSES).default(DEFAULT_COURSE),
  recipeId: z.uuid().nullable().default(null),
  freeText: z.string().max(160).default(''),
})

export type Dish = z.infer<typeof dishSchema>

export const payloadSchemas = {
  recipe: z.object({
    name: z.string().min(1).max(160),
    /**
     * Las cantidades van escritas para el hogar tal cual se cocina: no se
     * escalan por comensales. Redondear "1,4 huevos" es una fuente de errores
     * que no compensa para una familia de tamaño estable.
     */
    ingredients: z.array(ingredientSchema).max(60).default([]),
    steps: z.string().max(8000).default(''),
    /** Franjas en las que tiene sentido proponerla. Vacío = cualquiera. */
    slots: z.array(z.enum(MEAL_SLOTS)).max(4).default([]),
    /**
     * Qué plato de la comida puede ser: primero, segundo, guarnición, postre.
     * Vacío = no se ha dicho, y al generar una semana cuenta como segundo.
     */
    courses: z.array(z.enum(COURSES)).max(COURSES.length).default([]),
  }),
  meal_plan: z.object({
    dayKey: dayKeySchema,
    slot: z.enum(MEAL_SLOTS),
    /** Cada plato de la franja es su propio evento: se cambia uno sin tocar los demás. */
    course: z.enum(COURSES).default(DEFAULT_COURSE),
    recipeId: z.uuid().nullable().default(null),
    /** Para lo que no es receta: "sobras", "fuera", "pizza". */
    freeText: z.string().max(160).default(''),
  }),
  meal_eaten: z.object({
    dayKey: dayKeySchema,
    slot: z.enum(MEAL_SLOTS),
    /**
     * Lo que se comió de verdad, plato a plato, que puede no ser lo
     * planificado. Marcar la franja como comida es un solo evento con todos
     * sus platos, no uno por plato: es un único hecho y un único toque.
     */
    dishes: z.array(dishSchema).max(COURSES.length).default([]),
    /**
     * La forma de antes de que hubiera varios platos: uno solo, sin `course`.
     * Se sigue aceptando y se lee como el segundo (ver `eatenDishes`).
     */
    recipeId: z.uuid().nullable().default(null),
    freeText: z.string().max(160).default(''),
    /** Esa franja no se comió en casa o directamente no se comió. */
    skipped: z.boolean().default(false),
  }),
  shopping_extra: z.object({
    weekKey: dayKeySchema,
    name: z.string().min(1).max(120),
    quantity: z.number().min(0).max(100_000).nullable().default(null),
    unit: z.enum(UNITS).nullable().default(null),
    section: z.enum(SHOPPING_SECTIONS).default('otros'),
  }),
  shopping_check: z.object({
    weekKey: dayKeySchema,
    /** Identidad de la línea: nombre normalizado y unidad. */
    itemKey: z.string().min(1).max(200),
    bought: z.boolean(),
  }),
} satisfies Record<EventType, z.ZodType>

export type PayloadOf<T extends EventType> = z.infer<(typeof payloadSchemas)[T]>

/**
 * El payload tal como se escribe, antes de aplicar los valores por defecto.
 *
 * Es lo que aceptan quienes crean eventos: al guardar una receta no hay que
 * repetir `steps: ''` ni `section: null` para que el esquema lo complete al leer.
 */
export type PayloadInput<T extends EventType> = z.input<(typeof payloadSchemas)[T]>

/** Instante ISO-8601 con desfase, p.ej. 2026-09-14T18:12:00.000Z */
const isoInstant = z.string().datetime({ offset: true })
/** Zona IANA del dispositivo que registró el evento. */
const ianaZone = z.string().min(1).max(64)

export const householdEventSchema = z
  .object({
    id: z.uuid(),
    type: z.enum(EVENT_TYPES),
    occurredAt: isoInstant,
    tz: ianaZone,
    payload: z.record(z.string(), z.unknown()).default({}),
    createdBy: z.uuid(),
    createdAt: isoInstant,
    updatedAt: isoInstant,
    deletedAt: isoInstant.nullable().default(null),
  })
  .superRefine((event, ctx) => {
    const parsed = payloadSchemas[event.type].safeParse(event.payload)
    if (!parsed.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['payload'],
        message: `payload inválido para "${event.type}": ${parsed.error.issues
          .map((i) => `${i.path.join('.')} ${i.message}`)
          .join(', ')}`,
      })
    }
  })

export type HouseholdEvent = z.infer<typeof householdEventSchema>

/**
 * El payload ya validado, o null si no cuadra con su tipo.
 *
 * Todo lo que lee eventos pasa por aquí: un evento escrito por una versión
 * vieja no debe tumbar una pantalla, simplemente no cuenta.
 */
export function payloadOf<T extends EventType>(
  event: Pick<HouseholdEvent, 'type' | 'payload'>,
  type: T,
): PayloadOf<T> | null {
  if (event.type !== type) return null
  const parsed = payloadSchemas[type].safeParse(event.payload)
  return parsed.success ? (parsed.data as PayloadOf<T>) : null
}

export function isProteinGroup(group: FoodGroup): group is ProteinGroup {
  return (PROTEIN_GROUPS as readonly string[]).includes(group)
}

export function sectionOf(ingredient: Pick<Ingredient, 'group' | 'section'>): ShoppingSection {
  if (ingredient.section) return ingredient.section
  return ingredient.group ? SECTION_BY_GROUP[ingredient.group] : 'otros'
}

/**
 * Los gramos que aporta un ingrediente al plato.
 *
 * Los mililitros se cuentan como gramos: para una proporción de plato, la
 * diferencia de densidad no cambia ninguna decisión. Las cucharadas y las
 * pizcas no se convierten: son cantidades de condimento cuyo peso no importa.
 */
export function gramsOf(ingredient: Ingredient): number | null {
  if (ingredient.grams !== null) return ingredient.grams
  if (ingredient.quantity === null) return null
  if (ingredient.unit === 'g' || ingredient.unit === 'ml') return ingredient.quantity
  return null
}

/**
 * Identidad de una línea de la compra: el mismo nombre con la misma unidad se
 * funde en una sola línea. Sin tildes ni mayúsculas, porque "Tomate" y "tomate"
 * son el mismo tomate.
 */
export function itemKeyOf(name: string, unit: Unit | null): string {
  return `${normaliseName(name)}|${unit ?? ''}`
}

export function normaliseName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
}

/**
 * Los platos de un registro de lo comido, también de los escritos con la forma
 * antigua de un solo plato. Se normaliza al leer y no se migra nada guardado.
 */
export function eatenDishes(payload: PayloadOf<'meal_eaten'>): Dish[] {
  if (payload.dishes.length > 0) return payload.dishes
  if (payload.recipeId || payload.freeText) {
    return [{ course: DEFAULT_COURSE, recipeId: payload.recipeId, freeText: payload.freeText }]
  }
  return []
}
